import React, { useState } from "react";
import {
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  createUserWithEmailAndPassword,
  sendEmailVerification,
} from "firebase/auth";
import { auth } from "../firebase";
import { trackEvent, trackLogin, trackRegistration } from "../utils/analytics";
import { createUserWithRole } from "../utils/userManagement";
import flowFrameLogo from "../data/FlowFrame Logo.png";

function LoginForm({ onLoginSuccess, onGoogleSignIn }) {
  // Login form state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [rememberMe, setRememberMe] = useState(false);
  const [showResetForm, setShowResetForm] = useState(false);
  const [activeTab, setActiveTab] = useState("login");

  // Sign-up form state
  const [displayName, setDisplayName] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isInstructor, setIsInstructor] = useState(false);
  const [loading, setLoading] = useState(false);

  const validateSignUpForm = () => {
    setError(null);
    
    if (!email || !password || !confirmPassword) {
      setError("All fields are required");
      return false;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address");
      return false;
    }

    if (!displayName.trim()) {
      setError("Name is required");
      return false;
    }
    
    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return false;
    }
    
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return false;
    }
    
    return true;
  };

  async function handleLoginSubmit(e) {
    e.preventDefault();
    setError(null);

    try {
      localStorage.setItem('lastLoginMethod', 'email');
      
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );

      trackLogin('email');
      trackEvent('Login', {
        method: 'email'
      });

      onLoginSuccess?.(userCredential.user);
      console.log("Logged in as:", userCredential.user.uid);
    } catch (err) {
      console.error("Login error:", err);
      setError(err.message);
    }
  }

  async function handleSignUpSubmit(e) {
    e.preventDefault();
    
    if (!validateSignUpForm()) {
      return;
    }
    
    setLoading(true);
    
    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );

      // Send a verification email. Without this, Firebase will silently
      // replace the password account with a Google one if the user later
      // signs in with Google for the same email. Failure here must NOT abort
      // signup — the Auth user already exists, and the in-app banner gives
      // the user another way to request the email.
      try {
        await sendEmailVerification(userCredential.user);
      } catch (verifyErr) {
        console.warn("Verification email send failed (signup continued):", verifyErr);
      }

      await createUserWithRole(
        userCredential.user.uid, 
        {
          email: email,
          displayName: displayName || email.split('@')[0],
          photoURL: null,
          subscriptionTier: "free"
        }, 
        isInstructor
      );
      
      trackRegistration('email');
      
      // Clear form
      setEmail("");
      setPassword("");
      setConfirmPassword("");
      setDisplayName("");
      setIsInstructor(false);
      
      onLoginSuccess?.(userCredential.user);
    } catch (error) {
      let errorMessage = "Failed to create account";
      
      switch (error.code) {
        case "auth/email-already-in-use":
          errorMessage = "Email already in use. Please use a different email or log in.";
          break;
        case "auth/invalid-email":
          errorMessage = "Invalid email format.";
          break;
        case "auth/weak-password":
          errorMessage = "Password is too weak. Please use a stronger password.";
          break;
        default:
          errorMessage = error.message || errorMessage;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }

  async function handleGoogleSignIn() {
    setError(null);
    try {
      if (activeTab === "signup") {
        trackRegistration('google');
      } else {
        trackLogin('google');
        trackEvent('Login', {
          method: 'google'
        });
      }

      // Use the Google sign-in handler from App.js
      await onGoogleSignIn?.();
    } catch (err) {
      console.error("Google sign-in error:", err);
      setError(err.message);
    }
  }

  async function handlePasswordReset(e) {
    e.preventDefault();
    setError(null);

    try {
      await sendPasswordResetEmail(auth, email);
      alert(
        "Password reset link has been sent to your email. Please check your inbox."
      );
      setShowResetForm(false);
    } catch (err) {
      console.error("Reset password error:", err);
      setError(err.message);
    }
  }

  const handleTabSwitch = (tab) => {
    setActiveTab(tab);
    setError(null); // Clear errors when switching tabs
  };

  if (showResetForm) {
    return (
      <div className="w-full max-w-md mx-auto">
        <div className="bg-white rounded-lg shadow-lg p-8">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center shadow-lg">
              <img src={flowFrameLogo} alt="FlowFrame" className="w-10 h-10" />
            </div>
          </div>

          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Reset your password
            </h1>
            <p className="text-gray-600">
              Enter your email and we'll send a link to reset your password.
            </p>
          </div>

          <form onSubmit={handlePasswordReset} className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="Enter your email"
              />
            </div>

            {error && (
              <div className="text-red-600 text-sm">{error}</div>
            )}

            <button
              type="submit"
              className="w-full bg-purple-600 text-white py-2 px-4 rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition duration-200 font-medium"
            >
              Send Reset Email
            </button>

            <button
              type="button"
              onClick={() => {
                setShowResetForm(false);
                setError(null);
              }}
              className="w-full text-purple-600 py-2 px-4 rounded-lg hover:bg-purple-50 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition duration-200"
            >
              Back to Login
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="bg-white rounded-lg shadow-lg p-8">
        {/* Logo */}
        <div className="flex justify-center mb-6">
          <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center shadow-lg">
            <img src={flowFrameLogo} alt="FlowFrame" className="w-10 h-10" />
          </div>
        </div>

        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {activeTab === "login" ? "Log in to your account" : "Create your account"}
          </h1>
          <p className="text-gray-600">
            {activeTab === "login" 
              ? "Welcome back! Please enter your details." 
              : "Welcome! Please fill in your details to get started."}
          </p>
        </div>

        {/* Tab Switcher */}
        <div className="flex bg-gray-100 rounded-lg p-1 mb-6">
          <button
            type="button"
            onClick={() => handleTabSwitch("signup")}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition duration-200 ${
              activeTab === "signup"
                ? "bg-white text-gray-900 shadow-sm"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            Sign up
          </button>
          <button
            type="button"
            onClick={() => handleTabSwitch("login")}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition duration-200 ${
              activeTab === "login"
                ? "bg-white text-gray-900 shadow-sm"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            Log in
          </button>
        </div>

        <form onSubmit={activeTab === "login" ? handleLoginSubmit : handleSignUpSubmit} className="space-y-6">
          {/* Name Field - Only for Sign Up */}
          {activeTab === "signup" && (
            <div>
              <label htmlFor="displayName" className="block text-sm font-medium text-gray-700 mb-2">
                Name
              </label>
              <input
                id="displayName"
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="Your name"
              />
            </div>
          )}

          {/* Email Field */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
              Email
            </label>
            <div className="relative">
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent pr-10"
                placeholder="Enter your email"
              />
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                <div className="w-6 h-6 bg-red-100 rounded flex items-center justify-center">
                  <span className="text-red-600 text-xs">⋯</span>
                </div>
              </div>
            </div>
          </div>

          {/* Password Field */}
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
              Password
            </label>
            <div className="relative">
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent pr-10"
                placeholder="••••••••"
              />
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                <div className="w-6 h-6 bg-red-100 rounded flex items-center justify-center">
                  <span className="text-red-600 text-xs">⋯</span>
                </div>
              </div>
            </div>
          </div>

          {/* Confirm Password Field - Only for Sign Up */}
          {activeTab === "signup" && (
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                Confirm Password
              </label>
              <div className="relative">
                <input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent pr-10"
                  placeholder="••••••••"
                />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                  <div className="w-6 h-6 bg-red-100 rounded flex items-center justify-center">
                    <span className="text-red-600 text-xs">⋯</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Instructor Checkbox - Only for Sign Up */}
          {activeTab === "signup" && (
            <div className="flex items-center">
              <input
                id="isInstructor"
                type="checkbox"
                checked={isInstructor}
                onChange={(e) => setIsInstructor(e.target.checked)}
                className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
              />
              <label htmlFor="isInstructor" className="ml-2 text-sm text-gray-600">
                I am an instructor
              </label>
            </div>
          )}

          {/* Remember Me & Forgot Password - Only for Login */}
          {activeTab === "login" && (
            <div className="flex items-center justify-between">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-600">Remember for 30 days</span>
              </label>
              <button
                type="button"
                onClick={() => {
                  setShowResetForm(true);
                  setError(null);
                }}
                className="text-sm text-purple-600 hover:text-purple-500"
              >
                Forgot password
              </button>
            </div>
          )}

          {error && (
            <div className="text-red-600 text-sm">{error}</div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className={`w-full bg-purple-600 text-white py-2 px-4 rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition duration-200 font-medium ${
              loading ? "opacity-70 cursor-not-allowed" : ""
            }`}
          >
            {loading 
              ? "Creating Account..." 
              : activeTab === "login" 
                ? "Sign in" 
                : "Create Account"}
          </button>

          {/* Google Sign In Button */}
          <button
            type="button"
            onClick={handleGoogleSignIn}
            className="w-full bg-white border border-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition duration-200 font-medium flex items-center justify-center space-x-2"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            <span>
              {activeTab === "login" ? "Sign in with Google" : "Sign up with Google"}
            </span>
          </button>
        </form>

        {/* Terms of Service - Only for Sign Up */}
        {activeTab === "signup" && (
          <p className="text-xs text-gray-500 mt-4 text-center">
            By signing up, you agree to our Terms of Service and Privacy Policy.
          </p>
        )}
      </div>
    </div>
  );
}

export default LoginForm;
